import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Check, Trash2, Star } from "lucide-react";
import { Card, CardContent } from "@tabipla/ui";
import { usePackingItems, usePackingFilters, usePackingEditMode } from "./PackingContext";

export function PackingItemsList() {
  const { t } = useTranslation();
  const {
    items,
    onToggleItem,
    onDeleteItem,
    onSetPriority,
    isToggling,
    isDeleting,
    isSettingPriority,
  } = usePackingItems();
  const { showUncheckedOnly, priorityFilter } = usePackingFilters();
  const { isEditMode, selectedItemIds, onToggleItemSelection } = usePackingEditMode();

  // Filter and sort items: priority filter, then unchecked filter, then sort
  const sortedItems = useMemo(() => {
    let filtered = items;

    // Apply priority filter
    if (priorityFilter !== "all") {
      if (priorityFilter === "none") {
        filtered = filtered.filter((item) => item.priority === null);
      } else {
        const priorityValue = parseInt(priorityFilter, 10);
        filtered = filtered.filter((item) => item.priority === priorityValue);
      }
    }

    // Apply unchecked filter
    if (showUncheckedOnly) {
      filtered = filtered.filter((item) => !item.isChecked);
    }

    return [...filtered].sort((a, b) => {
      // Higher priority first (3 > 2 > 1 > null)
      const aPriority = a.priority ?? 0;
      const bPriority = b.priority ?? 0;
      if (aPriority !== bPriority) {
        return bPriority - aPriority;
      }
      // Unchecked items first
      if (a.isChecked !== b.isChecked) {
        return a.isChecked ? 1 : -1;
      }
      // Then by order
      return a.order - b.order;
    });
  }, [items, showUncheckedOnly, priorityFilter]);

  if (sortedItems.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardContent className="p-0">
        <div className="divide-y">
          {sortedItems.map((item) => (
            <div
              key={item.id}
              className={`flex items-center gap-3 px-3 py-2 hover:bg-muted/50 ${
                isEditMode && selectedItemIds.has(item.id) ? "bg-primary/5" : ""
              }`}
            >
              {isEditMode ? (
                <button
                  onClick={() => onToggleItemSelection(item.id)}
                  className={`-ml-2 flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded transition-colors ${
                    selectedItemIds.has(item.id)
                      ? "text-primary"
                      : "text-muted-foreground/30 hover:text-primary"
                  }`}
                >
                  <span
                    className={`flex h-5 w-5 items-center justify-center rounded border-2 ${
                      selectedItemIds.has(item.id)
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-current"
                    }`}
                  >
                    {selectedItemIds.has(item.id) && <Check className="h-3 w-3" />}
                  </span>
                </button>
              ) : (
                <button
                  onClick={() => onToggleItem(item.id)}
                  className="-ml-2 flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded transition-colors"
                  disabled={isToggling}
                >
                  <span
                    className={`flex h-6 w-6 items-center justify-center rounded-full border-2 ${
                      item.isChecked
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-muted-foreground/30 hover:border-primary"
                    }`}
                  >
                    {item.isChecked && <Check className="h-3 w-3" />}
                  </span>
                </button>
              )}
              <span
                className={`flex-1 ${item.isChecked ? "text-muted-foreground line-through" : ""}`}
              >
                {item.name}
                {item.quantity > 1 && (
                  <span className="ml-1 text-sm text-muted-foreground">×{item.quantity}</span>
                )}
              </span>
              {!isEditMode && (
                <>
                  <div className="-mr-2 flex items-center">
                    {[1, 2, 3].map((starNum) => (
                      <button
                        key={starNum}
                        onClick={() => {
                          // If clicking the same star that is currently set, clear priority
                          const newPriority = item.priority === starNum ? null : starNum;
                          onSetPriority(item.id, newPriority);
                        }}
                        className={`flex min-h-[44px] min-w-[36px] items-center justify-center transition-colors ${
                          item.priority !== null && starNum <= item.priority
                            ? "text-amber-400 hover:text-amber-500"
                            : "text-muted-foreground/30 hover:text-amber-400"
                        }`}
                        disabled={isSettingPriority}
                        title={t("packing.setPriority", { count: starNum })}
                      >
                        <Star
                          className={`h-4 w-4 ${item.priority !== null && starNum <= item.priority ? "fill-current" : ""}`}
                        />
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => onDeleteItem(item.id)}
                    className="-mr-2 flex min-h-[44px] min-w-[44px] items-center justify-center rounded text-muted-foreground hover:text-destructive"
                    disabled={isDeleting}
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
